import React, { SyntheticEvent, useCallback, useEffect } from 'react';
import _get from 'lodash/get';
import {
  AvailabilityTileSize,
  ProviderAvailability,
  Slot,
  getProviderImageUrl,
  AppointmentInfo,
  PurposeOptionsByRelationship
} from '@kyruus/provider-components';

import Link from '@kyruus/link';
import { PMCConfig } from '@kyruus/types';
import { FormattedMessage, useIntl } from '@kyruus/intl';
import { formatAvailabilityForTimeTiles } from '@kyruus/availability-tiles';

import { AppointmentSlot, ExtendedProvider, Log } from 'Src/types';
import { getProviderSpecialties } from 'Src/utils/provider';
import {
  Header as AvailabilityHeader,
  ProviderSummary,
  ProviderImage,
  ProviderName,
  ProviderSpecialties,
  DrawerHeader,
  Content,
  Footer
} from './styles';
import { messages } from './messages';
import {
  AVAILABILITY_DRAWER,
  TRACKING_EVENTS,
  MAX_AVAILABILITIES_TO_DISPLAY
} from '../constants';
import { useAvailability } from './useAvailbility';
import { Loading } from './Loading';
import { useImageService } from '@kyruus/image-component';
import { AppDrawer } from '../../../app-drawer/AppDrawer';
import { APP_DRAWER_IDS, useAppDrawer } from 'Src/app-drawer';
import Button from '@kyruus/button';
import Cancel from '@kyruus/react-ikons/Cancel';

interface AvailabilityDrawerProps {
  bookOnlineUrl: string;
  config: PMCConfig;
  provider: ExtendedProvider;
  appointmentInfo: AppointmentInfo;
  onClose: (providerId: number, reason: string) => void;
  log: Log;
  onViewMoreClick: (
    event: SyntheticEvent,
    data: {
      providerId?: string;
      viewMoreBookOnlineUrl?: string;
      [key: string]: unknown;
    }
  ) => void;
  fetchProviderAvailability: (
    providerIds: number,
    appointmentInfo: AppointmentInfo
  ) => Promise<{ slots: AppointmentSlot[]; error?: boolean }>;
  selectedDate: string;
  onTimeSlotsClick: (
    event: SyntheticEvent,
    data: {
      providerId: string;
      date?: string;
      time?: string;
      href?: string;
      unformattedDate?: string;
      locationId?: string;
      patient_relationship?: string;
      [key: string]: unknown;
    }
  ) => void;
  purposeOptionsByProviderId: Record<number, PurposeOptionsByRelationship>;
  availabilityError: string;
  purposesLoading: boolean;
  fetchPurposesByProviderId: (providerId: number, log: Log) => Promise<void>;
}

export const AvailabilityDrawer = ({
  appointmentInfo,
  bookOnlineUrl,
  config,
  provider,
  onClose,
  log,
  onViewMoreClick,
  fetchProviderAvailability,
  selectedDate,
  onTimeSlotsClick,
  purposeOptionsByProviderId,
  fetchPurposesByProviderId,
  availabilityError,
  purposesLoading
}: AvailabilityDrawerProps) => {
  const {
    config: { enableEnhancement }
  } = useImageService();
  const {
    providerName,
    providerPhoneNumber,
    renderSlotsAsButtons,
    appointmentOptions,
    loading
  } = useAvailability({
    config,
    provider,
    appointmentInfo,
    purposeOptionsByProviderId,
    fetchPurposesByProviderId,
    availabilityError,
    purposesLoading,
    log
  });

  const { openAppDrawer, isAppDrawerOpen } = useAppDrawer();

  const closeButtonRef = useCallback((closeButtonNode: HTMLElement | null) => {
    if (closeButtonNode) {
      closeButtonNode.focus();
    }
  }, []);

  const intl = useIntl();
  useEffect(() => {
    if (provider) {
      setTimeout(() => {
        openAppDrawer(APP_DRAWER_IDS.AVAILABILITY_DRAWER);
      }, 1);
    }
  }, [provider]);

  if (!provider) {
    return null;
  }

  let initialAppointmentInfo = appointmentOptions?.initialAppointmentInfo;
  if (!config.feature_flags?.use_defaults_for_booking_questions) {
    initialAppointmentInfo = {};
  }

  const handleOnClose = (_e: SyntheticEvent, reason: string) => {
    onClose(provider.id, reason);
  };
  const handlePhoneNumberClick = () => {
    log(TRACKING_EVENTS.PHONE_NUMBER_CLICK, {
      providerId: provider.id
    });
  };
  const handleTimeSlotsClick = (
    event: SyntheticEvent,
    data: {
      providerId: string;
      date?: string;
      time?: string;
      href?: string;
      unformattedDate?: string;
      locationId?: string;
      patient_relationship?: string;
      [key: string]: unknown;
    }
  ) => {
    onTimeSlotsClick(event, {
      ...data,
      provider_id: data.providerId,
      provider
    });
  };
  const showRegulatedSpacesMessage = _get(
    config,
    'direct_book.show_regulated_spaces_banner'
  );
  if (!isAppDrawerOpen) {
    return null;
  }
  return (
    <AppDrawer
      data-testid={AVAILABILITY_DRAWER.ID}
      id={APP_DRAWER_IDS.AVAILABILITY_DRAWER}
      onClose={handleOnClose}
      aria-labelledby={'availability-drawer-title'}
    >
      <div>
        <DrawerHeader>
          <FormattedMessage {...messages.drawerTitle}>
            {(message) => (
              <AvailabilityHeader id="availability-drawer-title">
                {message}
              </AvailabilityHeader>
            )}
          </FormattedMessage>
          <Button
            onClick={(e: SyntheticEvent) => handleOnClose(e, 'closeClick')}
            mode="flat"
            data-testid="close-drawer-button"
            ref={closeButtonRef}
            aria-label={intl.formatMessage(messages.closeDrawer)}
          >
            <Cancel size="1.6em" aria-hidden="true" />
          </Button>
        </DrawerHeader>
        <Content>
          <ProviderSummary>
            <ProviderImage
              alt={providerName}
              src={getProviderImageUrl(provider)}
              desiredWidth={48}
              desiredHeight={48}
              enhancementEnabled={enableEnhancement}
              data-testid="provider-image"
            />
            <ProviderName>{providerName}</ProviderName>
            <ProviderSpecialties>
              {getProviderSpecialties(provider)}
            </ProviderSpecialties>
          </ProviderSummary>
          <Loading loading={loading} availabilityError={availabilityError} />
          {loading === false && appointmentOptions && (
            <ProviderAvailability
              appointmentInfo={
                initialAppointmentInfo ? initialAppointmentInfo : {}
              }
              locations={provider.locations}
              loggingFunction={log}
              maxAvailabilitiesToDisplay={MAX_AVAILABILITIES_TO_DISPLAY}
              provider={provider}
              config={config}
              purposeOptionsByRelationship={
                appointmentOptions.purposeOptionsByRelationship
              }
              selectedDate={selectedDate}
              services={{
                formatAvailability: (slots: Slot[]) =>
                  formatAvailabilityForTimeTiles(slots, bookOnlineUrl, false),
                fetchAvailability: fetchProviderAvailability
              }}
              shouldRenderSlotsAsButtons={renderSlotsAsButtons}
              showRegulatedSpacesMessage={showRegulatedSpacesMessage}
              tileSize={AvailabilityTileSize.SMALL}
              onTimeSlotsClick={handleTimeSlotsClick}
              bookOnlineUrl={bookOnlineUrl}
              onViewMoreClick={onViewMoreClick}
            />
          )}
        </Content>
        {providerPhoneNumber && (
          <Footer>
            <FormattedMessage
              {...messages.phoneNumber}
              values={{
                phone: (
                  <Link
                    key="phone"
                    href={`tel:${providerPhoneNumber}`}
                    onClick={handlePhoneNumberClick}
                  >
                    {providerPhoneNumber}
                  </Link>
                )
              }}
            />
          </Footer>
        )}
      </div>
    </AppDrawer>
  );
};

AvailabilityDrawer.displayName = 'AvailabilityDrawer';
